import { type FormEvent, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

import type { TypeOrganisation } from "../api/types";
import { utiliserSession } from "../auth/SessionContext";

export function AuthentificationPage({ mode }: { mode: "connexion" | "inscription" }) {
  const { session, connecter, inscrire } = utiliserSession();
  const naviguer = useNavigate();
  const [email, definirEmail] = useState("");
  const [motDePasse, definirMotDePasse] = useState("");
  const [nomUtilisateur, definirNomUtilisateur] = useState("");
  const [nomOrganisation, definirNomOrganisation] = useState("");
  const [typeOrganisation, definirTypeOrganisation] =
    useState<TypeOrganisation>("PRODUCTEUR");
  const [erreur, definirErreur] = useState("");
  const [chargement, definirChargement] = useState(false);

  if (session) return <Navigate to="/espace" replace />;

  async function soumettre(evenement: FormEvent) {
    evenement.preventDefault();
    definirErreur("");
    definirChargement(true);
    try {
      if (mode === "connexion") {
        await connecter(email, motDePasse);
      } else {
        await inscrire({
          email,
          mot_de_passe: motDePasse,
          nom_utilisateur: nomUtilisateur,
          nom_organisation: nomOrganisation,
          type_organisation: typeOrganisation
        });
      }
      naviguer("/espace");
    } catch (cause) {
      definirErreur(cause instanceof Error ? cause.message : "Connexion impossible");
    } finally {
      definirChargement(false);
    }
  }

  return (
    <section className="auth-page">
      <div className="auth-page__promesse">
        <span className="sur-titre">
          {mode === "connexion" ? "Heureux de vous revoir" : "Rejoignez le réseau"}
        </span>
        <h1>
          {mode === "connexion"
            ? "Votre saison continue ici."
            : "Donnez plus de portée à chaque spectacle."}
        </h1>
        <p>
          Un espace gratuit pour préparer, partager et analyser vos programmations,
          avec des données dont l’origine reste lisible.
        </p>
        <ul className="liste-cochee">
          <li>Fiches publiques partageables</li>
          <li>Mise en relation directe</li>
          <li>Recettes privées par défaut</li>
        </ul>
      </div>
      <form className="auth-card" onSubmit={soumettre}>
        <h2>{mode === "connexion" ? "Se connecter" : "Créer mon espace"}</h2>
        {mode === "inscription" && (
          <>
            <fieldset className="choix-role">
              <legend>Je représente</legend>
              <label>
                <input
                  type="radio"
                  name="type"
                  value="PRODUCTEUR"
                  checked={typeOrganisation === "PRODUCTEUR"}
                  onChange={() => definirTypeOrganisation("PRODUCTEUR")}
                />
                <span>
                  <strong>Une compagnie</strong>
                  <small>Je diffuse des productions</small>
                </span>
              </label>
              <label>
                <input
                  type="radio"
                  name="type"
                  value="STRUCTURE"
                  checked={typeOrganisation === "STRUCTURE"}
                  onChange={() => definirTypeOrganisation("STRUCTURE")}
                />
                <span>
                  <strong>Une structure</strong>
                  <small>Je construis une saison</small>
                </span>
              </label>
            </fieldset>
            <label className="champ">
              <span>Votre nom</span>
              <input
                required
                minLength={2}
                autoComplete="name"
                value={nomUtilisateur}
                onChange={(e) => definirNomUtilisateur(e.target.value)}
              />
            </label>
            <label className="champ">
              <span>Nom de l’organisation</span>
              <input
                required
                minLength={2}
                autoComplete="organization"
                value={nomOrganisation}
                onChange={(e) => definirNomOrganisation(e.target.value)}
              />
            </label>
          </>
        )}
        <label className="champ">
          <span>Adresse électronique</span>
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => definirEmail(e.target.value)}
          />
        </label>
        <label className="champ">
          <span>Mot de passe</span>
          <input
            type="password"
            required
            minLength={mode === "inscription" ? 12 : 1}
            autoComplete={mode === "inscription" ? "new-password" : "current-password"}
            value={motDePasse}
            onChange={(e) => definirMotDePasse(e.target.value)}
          />
          {mode === "inscription" && (
            <small>12 caractères minimum, avec une lettre et un chiffre.</small>
          )}
        </label>
        {erreur && <p className="alerte alerte--erreur">{erreur}</p>}
        <button className="bouton bouton--primaire bouton--large" disabled={chargement}>
          {chargement
            ? "Un instant…"
            : mode === "connexion"
              ? "Accéder à mon espace"
              : "Créer mon espace gratuit"}
        </button>
        <p className="auth-card__bascule">
          {mode === "connexion" ? (
            <>
              Première visite ? <Link to="/inscription">Créer un compte</Link>
            </>
          ) : (
            <>
              Déjà membre ? <Link to="/connexion">Se connecter</Link>
            </>
          )}
        </p>
      </form>
    </section>
  );
}
